import { Chart, ChartGroup } from "@/app/types"

import { isArray, isString } from "@/app/utils"

// Chart Definition - code, name, slug, chartType, fundOnly, firmOnly, top10Metric, metrics[]
// common metric names   - aum, aumChangePcent, cumeNetflow, mktSharePcent, netflow, organicGrowthPcent
// fundOnly metric names - expenseRatioPcent, returnPcent, returnPtile
// firmOnly metric names - avgAum, numFunds
// make all methods static so we can initialize chart lists below

export const GrowthCharts: ChartGroup = new ChartGroup({
  groupname: "Growth",
  slug: "growth",
  groupMetrics: ["organicGrowthPcent"],
  charts: [
    new Chart({
      code: "_1a_growth_organic_growth",
      name: "Organic Growth",
      slug: "OrgGrowth",
      chartType: "periodic",
      applyTo: "both",
      top10: "organicGrowthPcent",
      chartMetrics: ["organicGrowthPcent"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_1b_growth_organic_growth_rank",
      name: "Organic Growth Rank",
      slug: "OrgGrowthRank",
      chartType: "bar",
      applyTo: "both",
      top10: "organicGrowthPcent",
      chartMetrics: ["organicGrowthPcent"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_1c_growth_aum_change",
      name: "AUM Change",
      slug: "AumChange",
      chartType: "periodic",
      applyTo: "both",
      top10: "aumChangePcent",
      chartMetrics: ["aumChangePcent"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_1d_growth_aum_change_rank",
      name: "AUM Change Rank",
      slug: "AumChangeRank",
      chartType: "bar",
      applyTo: "both",
      top10: "aumChangePcent",
      chartMetrics: ["aumChangePcent"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_1e_growth_org_growth_aum_change_mkt_share",
      name: "Org Growth|AUM Change|Mkt Share",
      slug: "OrgGrowthPos",
      chartType: "bubble",
      applyTo: "both",
      top10: "organicGrowthPcent",
      chartMetrics: ["organicGrowthPcent", "aumChangePcent", "mktSharePcent"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
  ],
})

export const NetFlowsCharts: ChartGroup = new ChartGroup({
  groupname: "Flows",
  slug: "flows",
  groupMetrics: ["netflow"],
  charts: [
    new Chart({
      code: "_2a_flows_net_flows",
      name: "Net Flows",
      slug: "NetFlows",
      chartType: "periodic",
      applyTo: "both",
      top10: "netflow",
      chartMetrics: ["netflow"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_2b_flows_net_flows_rank",
      name: "Net Flows Rank",
      slug: "NetFlowsRank",
      chartType: "bar",
      applyTo: "both",
      top10: "netflow",
      chartMetrics: ["netflow"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_2c_flows_cumulative_flows",
      name: "Cumulative Flows",
      slug: "CumeFlows",
      chartType: "periodic",
      applyTo: "both",
      top10: "cumeNetflow",
      chartMetrics: ["cumeNetflow"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_2e_flows_net_flows_aum_change",
      name: "Net Flows|AUM Change",
      slug: "NetFlowsAumChange",
      chartType: "scatter",
      applyTo: "both",
      top10: "netflow",
      chartMetrics: ["netflow", "aumChangePcent"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
  ],
})

export const SizeCharts: ChartGroup = new ChartGroup({
  groupname: "Assets",
  slug: "assets",
  groupMetrics: ["aum"],
  charts: [
    new Chart({
      code: "_3a_assets_aum",
      name: "AUM",
      slug: "AUM",
      chartType: "periodic",
      applyTo: "both",
      top10: "aum",
      chartMetrics: ["aum"],
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
    new Chart({
      code: "_3b_assets_aum_rank",
      name: "AUM Rank",
      slug: "AUMRank",
      chartType: "bar",
      applyTo: "both",
      top10: "aum",
      chartMetrics: ["aum"],
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
    new Chart({
      code: "_3c_assets_fund_count",
      name: "Fund Count",
      slug: "FundCount",
      chartType: "periodic",
      applyTo: "firm",
      top10: "numFunds",
      chartMetrics: ["numFunds"],
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
    new Chart({
      code: "_3d_assets_fund_count_rank",
      name: "Fund Count Rank",
      slug: "FundCountRank",
      chartType: "bar",
      applyTo: "firm",
      top10: "numFunds",
      chartMetrics: ["numFunds"],
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
    new Chart({
      code: "_3e_assets_avg_aum_per_fund",
      name: "Average AUM per Fund",
      slug: "AvgAUM",
      chartType: "periodic",
      applyTo: "firm",
      top10: "avgAum",
      chartMetrics: ["avgAum"],
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
    new Chart({
      code: "_3f_assets_avg_aum_per_fund_rank",
      name: "Average AUM per Fund Rank",
      slug: "AvgAUMRank",
      chartType: "bar",
      applyTo: "firm",
      top10: "avgAum",
      chartMetrics: ["avgAum"],
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
    new Chart({
      code: "_3g_assets_fund_count_avg_fund_size",
      name: "Fund Count|Avg Fund Size",
      slug: "FundCountAvgAum",
      chartType: "scatter",
      applyTo: "firm",
      top10: "avgAum",
      chartMetrics: ["avgAum", "numFunds"],
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
  ],
})

export const MktShareCharts: ChartGroup = new ChartGroup({
  groupname: "Mkt Share",
  slug: "mktShare",
  groupMetrics: ["mktSharePcent"],
  charts: [
    new Chart({
      code: "_4a_mkt_share_market_share",
      name: "Market Share",
      slug: "MktShare",
      chartType: "periodic",
      applyTo: "both",
      top10: "mktSharePcent",
      chartMetrics: ["mktSharePcent"],
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
    new Chart({
      code: "_4b_mkt_share_market_share_rank",
      name: "Market Share Rank",
      slug: "MktShareRank",
      chartType: "bar",
      applyTo: "both",
      top10: "mktSharePcent",
      chartMetrics: ["mktSharePcent"],
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
    new Chart({
      code: "_4c_mkt_share_market_share_exp_ratio",
      name: "Market Share|Exp Ratio",
      slug: "MktShareExpRatio",
      chartType: "scatter",
      applyTo: "fund",
      top10: "mktSharePcent",
      chartMetrics: ["mktSharePcent", "expenseRatioPcent"],
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
    new Chart({
      code: "_4d_mkt_share_mkt_share_avg_fund_aum_aum",
      name: "MKt Share|Avg Fund AUM|AUM",
      slug: "MktSharePos",
      chartType: "bubble",
      applyTo: "firm",
      top10: "mktSharePcent",
      chartMetrics: ["mktSharePcent", "avgAum", "aum"],
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
  ],
})

export const FeeCharts: ChartGroup = new ChartGroup({
  groupname: "Fees",
  slug: "fee",
  groupMetrics: ["expenseRatioPcent"],
  charts: [
    // ID 11/29/23 - Removing the periodic expense ratio chart since we dont have historical values
    // new Chart({ code:"5a", name:"Expense Ratio", slug:"ExpRatio", chartType:"periodic", applyTo: "fund",
    //     top10:"expenseRatioPcent", chartMetrics:["expenseRatioPcent"]
    // }),
    new Chart({
      code: "_5b_fees_expense_ratio_rank",
      name: "Expense Ratio Rank",
      slug: "ExpRatioRank",
      chartType: "bar",
      applyTo: "fund",
      top10: "expenseRatioPcent",
      chartMetrics: ["expenseRatioPcent"],
      supportsDaily: true,
      isTermDurationRelevant: false,
    }),
    new Chart({
      code: "_5c_fees_exp_ratio_organic_growth",
      name: "Exp Ratio|Organic Growth",
      slug: "ExpRatioOrgGrowth",
      chartType: "scatter",
      applyTo: "fund",
      top10: "expenseRatioPcent",
      chartMetrics: ["expenseRatioPcent", "organicGrowthPcent"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_5d_fees_exp_ratio_tr_mkt_share",
      name: "Exp Ratio|TR|Mkt Share",
      slug: "FeeReturn",
      chartType: "bubble",
      applyTo: "fund",
      top10: "expenseRatioPcent",
      chartMetrics: ["expenseRatioPcent", "returnPcent", "mktSharePcent"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_5e_fees_exp_ratio_tr_percentile_aum",
      name: "Exp Ratio|TR Percentile|AUM",
      slug: "FeeReturnPtile",
      chartType: "bubble",
      applyTo: "fund",
      top10: "expenseRatioPcent",
      chartMetrics: ["expenseRatioPcent", "returnPtile", "aum"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_5f_fees_exp_ratio_org_growth_mkt_share",
      name: "Exp Ratio|Org Growth|Mkt Share",
      slug: "FeeOrgGrowth",
      chartType: "bubble",
      applyTo: "fund",
      top10: "expenseRatioPcent",
      chartMetrics: [
        "expenseRatioPcent",
        "organicGrowthPcent",
        "mktSharePcent",
      ],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
  ],
})

export const PerformanceCharts: ChartGroup = new ChartGroup({
  groupname: "Performance",
  slug: "perf",
  groupMetrics: ["returnPcent"],
  charts: [
    new Chart({
      code: "_6a_performance_total_return",
      name: "Total Return",
      slug: "Return",
      chartType: "periodic",
      applyTo: "fund",
      top10: "returnPcent",
      chartMetrics: ["returnPcent"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_6b_performance_total_return_rank",
      name: "Total Return Rank",
      slug: "ReturnRank",
      chartType: "bar",
      applyTo: "fund",
      top10: "returnPcent",
      chartMetrics: ["returnPcent"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_6c_performance_total_return_percentile",
      name: "Total Return Percentile",
      slug: "ReturnPtile",
      chartType: "periodic",
      applyTo: "fund",
      top10: "returnPtile",
      chartMetrics: ["returnPtile"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_6d_performance_total_return_percentile_rank",
      name: "Total Return Percentile Rank",
      slug: "ReturnPtileRank",
      chartType: "bar",
      applyTo: "fund",
      top10: "returnPtile",
      chartMetrics: ["returnPtile"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_6e_performance_total_return_net_flows",
      name: "Total Return|Net Flows",
      slug: "FeeReturnPtile",
      chartType: "scatter",
      applyTo: "fund",
      top10: "returnPcent",
      chartMetrics: ["returnPcent", "netflow"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_6f_performance_tr_return_percentile_organic_growth",
      name: "TR Percentile|Organic growth",
      slug: "FeeOrgGrowth",
      chartType: "scatter",
      applyTo: "fund",
      top10: "returnPtile",
      chartMetrics: ["returnPtile", "organicGrowthPcent"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_6g_performance_tr_return_percentile_expense_ratio",
      name: "TR Percentile|Expense Ratio",
      slug: "FeeReturn",
      chartType: "scatter",
      applyTo: "fund",
      top10: "returnPtile",
      chartMetrics: ["returnPtile", "expenseRatioPcent"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_6h_performance_tr_return_percentile_org_growth_aum",
      name: "TR Percentile|Org Growth|AUM",
      slug: "ReturnOrgGrowthAum",
      chartType: "bubble",
      applyTo: "fund",
      top10: "returnPtile",
      chartMetrics: ["returnPtile", "organicGrowthPcent", "aum"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
    new Chart({
      code: "_6i_performance_tr_return_percentile_tr_aum",
      name: "TR Percentile|TR|AUM",
      slug: "ReturnAum",
      chartType: "bubble",
      applyTo: "fund",
      top10: "returnPtile",
      chartMetrics: ["returnPtile", "returnPcent", "aum"],
      supportsDaily: true,
      isTermDurationRelevant: true,
    }),
  ],
})

export const ALL_CHARTGROUPS: ChartGroup[] = [
  GrowthCharts,
  NetFlowsCharts,
  SizeCharts,
  MktShareCharts,
  FeeCharts,
  PerformanceCharts,
]

// add the group back reference to all the charts, be careful of circular references
ALL_CHARTGROUPS.forEach((group) =>
  group.charts.forEach((chart) => {
    chart.group = group
  })
)

export const ALL_CHARTS: Chart[] = ALL_CHARTGROUPS.map(
  (group) => group.charts
).flat()

// create a list ChartGroups which only have charts applicable to Funds
export const FUND_CHARTGROUPS = ALL_CHARTGROUPS
  // first create a new ChartGroup() with only the filtered charts list
  .map((g) => {
    return new ChartGroup({
      groupname: g.groupname,
      slug: g.slug,
      groupMetrics: g.chartGroupMetrics
        .filter((m) => !m.firmOnly)
        .map((m) => m.name),
      charts: g.charts.filter((c) => c.isShown(true)),
    })
  })
  // then keep only the groups which have more than 0 charts
  .filter((g) => g.charts.length > 0)

export const FIRM_CHARTGROUPS = ALL_CHARTGROUPS.map(
  (g) =>
    new ChartGroup({
      groupname: g.groupname,
      slug: g.slug,
      groupMetrics: g.chartGroupMetrics
        .filter((m) => !m.fundOnly)
        .map((m) => m.name),
      charts: g.charts.filter((c) => c.isShown(false)),
    })
).filter((g) => g.charts.length > 0)

export function findChartGroup(
  slug: string,
  chartgroups: ChartGroup[] = ALL_CHARTGROUPS
): ChartGroup {
  return isString(slug) && isArray(chartgroups) ?
      chartgroups.find((group) => group?.slug == slug)
    : null
}

export function findChart(code: string, charts: Chart[] = ALL_CHARTS): Chart {
  return isString(code) && isArray(charts) ?
      charts.find((chart) => chart?.code == code)
    : null
}
